import React, { useState, useEffect } from 'react';
import { plantNames as defaultPlantNames } from '../../constants/property';

const DefaultSettings = () => {
  const [plantNames, setPlantNames] = useState(defaultPlantNames);
  const [gstValue, setGstValue] = useState(5); // Default GST value is 5%
  const [newPlantName, setNewPlantName] = useState('');
  const [newPlantValue, setNewPlantValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedPlants = localStorage.getItem('plantNames');
    const savedGST = localStorage.getItem('gstValue');
    
    if (savedPlants) {
      try {
        setPlantNames(JSON.parse(savedPlants));
      } catch (e) {
        console.error('Error loading plant names:', e);
      }
    }
    
    if (savedGST) {
      try {
        setGstValue(parseFloat(savedGST));
      } catch (e) {
        console.error('Error loading GST value:', e);
      }
    }
  }, []);

  const addPlant = () => {
    const name = newPlantName.trim();
    const value = newPlantValue.trim() || name;
    
    if (!name) {
      alert('Please enter a plant name');
      return;
    }
    
    // Check if plant already exists
    if (plantNames.some(p => p.name.toLowerCase() === name.toLowerCase() || p.value.toLowerCase() === value.toLowerCase())) {
      alert('Plant already exists');
      return;
    }
    
    const newPlant = {
      name: name,
      value: value,
      label: name
    };
    
    setPlantNames([...plantNames, newPlant]);
    setNewPlantName('');
    setNewPlantValue('');
  };

  const removePlant = (index) => {
    if (plantNames.length <= 1) {
      alert('At least one plant must exist');
      return;
    }
    setPlantNames(plantNames.filter((_, i) => i !== index));
  };

  const updatePlant = (index, field, value) => {
    const updated = [...plantNames];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'name') {
      updated[index].label = value;
    }
    setPlantNames(updated);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Save to localStorage
      localStorage.setItem('plantNames', JSON.stringify(plantNames));
      localStorage.setItem('gstValue', gstValue.toString());
      
      // Here you can also save to API if needed
      await new Promise((r) => setTimeout(r, 600));
      
      console.log('Default settings saved:', { plantNames, gstValue });
      setSuccessMessage('Settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (e) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    if (window.confirm('Are you sure you want to reset to default settings? This will remove all custom plants and reset GST value.')) {
      setPlantNames(defaultPlantNames);
      setGstValue(5);
      localStorage.removeItem('plantNames');
      localStorage.removeItem('gstValue');
      setSuccessMessage('Settings reset to defaults!');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Default Settings</h2>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {/* Plant Data */}
      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Plant Data</h3>
        
        {/* Existing Plants */}
        <div className="space-y-2">
          {plantNames.map((plant, index) => (
            <div key={index} className="flex items-center gap-2 p-2 border rounded">
              <input
                type="text"
                value={plant.name}
                onChange={(e) => updatePlant(index, 'name', e.target.value)}
                className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Plant Name"
              />
              <input
                type="text"
                value={plant.value}
                onChange={(e) => updatePlant(index, 'value', e.target.value)}
                className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Plant Value"
              />
              <button
                onClick={() => removePlant(index)}
                disabled={plantNames.length <= 1}
                className="px-3 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Remove Plant"
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        {/* Add New Plant */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Add New Plant</h4>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newPlantName}
              onChange={(e) => setNewPlantName(e.target.value)}
              placeholder="Plant Name"
              className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              type="text"
              value={newPlantValue}
              onChange={(e) => setNewPlantValue(e.target.value)}
              placeholder="Plant Value (optional)"
              className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={addPlant}
              className="px-4 py-2 bg-primary text-white rounded-md hover:opacity-90"
            >
              Add Plant
            </button>
          </div>
        </div>
      </div>

      {/* GST Value */}
      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">GST Value</h3>
        <div className="flex items-center gap-4">
          <label className="text-sm text-gray-700">GST Percentage:</label>
          <input
            type="number"
            value={gstValue}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (!isNaN(value) && value >= 0 && value <= 100) {
                setGstValue(value);
              }
            }}
            min="0"
            max="100"
            step="0.01"
            className="w-32 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <span className="text-sm text-gray-600">%</span>
        </div>
        <p className="text-xs text-gray-500">
          This GST value will be used as the default for calculations across the application.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          className={`px-4 py-2 rounded-md text-white ${saving ? 'bg-primary/70' : 'bg-primary hover:opacity-90'}`}
          style={{ backgroundColor: saving ? '#00968880' : '#009688' }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <button
          onClick={onReset}
          disabled={saving}
          className="px-4 py-2 rounded-md border hover:bg-gray-50"
        >
          Reset to Defaults
        </button>
      </div>
    </div>
  );
};

export default DefaultSettings;

